import {
  NII_APPROVED_BEFORE_SIGNOFF_NOTE,
  NII_SIGNOFF_FIELD_LABELS,
  niiReviewMilestoneValues,
  niiSignoffValues,
} from 'features/libra/stepComponents/niiReport/content/buildNiiReviewContent'
import type {
  NiiReportMeta,
  NiiReportSection,
  NiiReviewMilestone,
  NiiSignoffEntry,
} from 'features/libra/stepComponents/niiReport/content/niiReportContentTypes'
import { ELIGIBILITY_LABELS } from 'features/libra/types/eligibility'
import type { PdfTableData } from 'features/reporting/pdfGeneration/components/shared/PdfTable'

const COLUMN_SEPARATOR = ' – '

/** Joins peer values inside one cell. Newlines wrap inside an Excel cell. */
const CELL_SEPARATOR = '\n'

export interface SheetTable {
  headers: ReadonlyArray<string>
  rows: ReadonlyArray<ReadonlyArray<string>>
}

export interface NiiSheetWorkbookRows {
  tickets: SheetTable
  zones: SheetTable
  history: SheetTable
}

/** One ticket's report content, plus the identity the sheets are keyed by. */
export interface NiiSheetTicket {
  ticketId: string
  ticketTitle: string
  meta: NiiReportMeta
  sections: ReadonlyArray<NiiReportSection>
}

/**
 * Ticket titles whose asset or site name never resolved. `addTicketIdentity`
 * writes a blank cell for those rather than inventing a value, so the panel
 * can warn up front instead of letting the gap vanish into the spreadsheet.
 */
export function findMissingTicketIdentity(
  tickets: ReadonlyArray<NiiSheetTicket>
): { assetId: ReadonlyArray<string>; site: ReadonlyArray<string> } {
  return {
    assetId: tickets
      .filter((ticket) => !ticket.meta.assetName)
      .map((ticket) => ticket.ticketTitle),
    site: tickets
      .filter((ticket) => !ticket.meta.siteName)
      .map((ticket) => ticket.ticketTitle),
  }
}

/**
 * A row under construction, keyed by column name. Collecting columns by name
 * rather than position is what lets tickets with different workflows land in
 * one sheet — `toSheetTable` reconciles them at the end.
 */
type NamedRow = Map<string, string>

function columnName(sectionTitle: string, label: string): string {
  return `${sectionTitle}${COLUMN_SEPARATOR}${label}`
}

function joinCell(values: ReadonlyArray<string>): string {
  return values.filter(Boolean).join(CELL_SEPARATOR)
}

/**
 * Sets a column, suffixing rather than overwriting if the name is taken.
 * Two form blocks in one step can legitimately share a row label (e.g. two
 * "Result" rows), and silently dropping the second would lose data.
 */
function setUnique(row: NamedRow, name: string, value: string): void {
  if (!row.has(name)) {
    row.set(name, value)
    return
  }
  let suffix = 2
  while (row.has(`${name} (${suffix})`)) suffix += 1
  row.set(`${name} (${suffix})`, value)
}

function addKeyValueTable(
  row: NamedRow,
  prefix: string,
  table: PdfTableData
): void {
  table.rows.forEach(({ cells }) => {
    const [label, value, justification] = cells
    if (!label) return
    // Plain `set`: parameter names are unique within such a table, and the
    // same table can arrive from both `zoneInventory` and `zoneAnalysis` for
    // one zone — last write wins rather than making a duplicate column.
    row.set(columnName(prefix, label), value ?? '')
    if (justification) {
      row.set(columnName(prefix, `${label} (justification)`), justification)
    }
  })
}

function addPeerTable(
  row: NamedRow,
  prefix: string,
  table: PdfTableData
): void {
  table.headers.forEach((header, columnIndex) => {
    const column = table.rows.map(({ cells }) => cells[columnIndex] ?? '')
    row.set(columnName(prefix, header), joinCell(column))
  })
}

function addTicketIdentity(row: NamedRow, ticket: NiiSheetTicket): void {
  row.set('Ticket ID', ticket.ticketId)
  row.set('Ticket', ticket.ticketTitle)
  row.set('Site', ticket.meta.siteName ?? '')
  row.set('Asset ID', ticket.meta.assetName ?? '')
}

function addSectionToTicketRow(row: NamedRow, section: NiiReportSection): void {
  section.questionLists?.forEach((list) => {
    list.answers.forEach((answer) =>
      setUnique(
        row,
        columnName(section.title, answer.question),
        answer.answer ? 'Yes' : 'No'
      )
    )
    const { override } = list
    if (override) {
      const name = columnName(
        section.title,
        `${list.title ?? 'Questions'} override`
      )
      const details: ReadonlyArray<[string, string | undefined]> = [
        [name, override.value],
        [`${name} (calculated)`, override.calculated],
        [`${name} justification`, override.justification],
        [`${name} (by)`, override.by],
        [`${name} (at)`, override.at],
      ]
      details.forEach(([column, value]) => {
        if (value) setUnique(row, column, value)
      })
    }
  })

  // Block titles are deliberately left out of column names: they are often
  // config ids like "section_1", and the row label alone is what identifies
  // the field to a reader.
  section.formBlocks?.forEach((block) =>
    block.rows.forEach((formRow) =>
      setUnique(row, columnName(section.title, formRow.label), formRow.value)
    )
  )

  // Only the ticket-level parts of zone analysis. `zones` go to their own
  // sheet, and `overview` is dropped — it is already one row per zone, which
  // the Zones sheet supersedes exactly.
  const analysis = section.zoneAnalysis
  if (analysis?.zoneBreakdownJustification) {
    setUnique(
      row,
      columnName(section.title, 'Zone Breakdown Justification'),
      analysis.zoneBreakdownJustification
    )
  }

  const closeout = section.reportCloseout
  if (closeout) {
    const setCloseout = (label: string, value: string | undefined): void => {
      if (value) setUnique(row, columnName(section.title, label), value)
    }
    setCloseout('Eligibility', ELIGIBILITY_LABELS[closeout.eligibility])
    setCloseout('Executive Summary', closeout.executiveSummary)
    setCloseout(
      'Pre-screening',
      closeout.screenedOutAtPreScreening
        ? 'Screened out at pre-screening'
        : undefined
    )
    setCloseout('Justification', closeout.justification)
    setCloseout('Internal Comments', closeout.internalComments)
    setCloseout('Recommendations', joinCell(closeout.recommendations))
  }

  if (section.signoff) addSignoffColumns(row, section.signoff)
  if (section.approvedBeforeSignoffRequired) {
    row.set('Sign-off Note', NII_APPROVED_BEFORE_SIGNOFF_NOTE)
  }
  section.reviewMilestones?.forEach((milestone) =>
    addReviewMilestoneColumns(row, milestone)
  )
}

/** E.g. "Approved By" and "Approved At". */
function addReviewMilestoneColumns(
  row: NamedRow,
  milestone: NiiReviewMilestone
): void {
  const [by, at] = niiReviewMilestoneValues(milestone)
  setUnique(row, `${milestone.label} By`, by)
  setUnique(row, `${milestone.label} At`, at)
}

/**
 * The PDF's sign-off table flattened: one column per slot and field, e.g.
 * "SME Review – Signed By". Slots come from the workflow config, so a
 * workflow without sign-off contributes no slot columns.
 */
function addSignoffColumns(
  row: NamedRow,
  signoff: ReadonlyArray<NiiSignoffEntry>
): void {
  signoff.forEach((entry) => {
    const values = niiSignoffValues(entry)
    NII_SIGNOFF_FIELD_LABELS.forEach((label, index) =>
      setUnique(row, columnName(entry.title, label), values[index])
    )
  })
}

function buildTicketRow(ticket: NiiSheetTicket): NamedRow {
  const row: NamedRow = new Map()
  addTicketIdentity(row, ticket)
  row.set('Analysis Date', ticket.meta.analysisDate ?? '')
  row.set('Analysis Version', String(ticket.meta.analysisVersion))
  ticket.sections.forEach((section) => addSectionToTicketRow(row, section))
  return row
}

/**
 * One row per zone, merged across sections by `zoneIndex`: a zone's design
 * tables can come from a ConditionalForm step's `zoneInventory` while its
 * coverage math comes from the ZoneAnalysis step, and its recommendations
 * from the closeout step.
 */
function buildZoneRows(ticket: NiiSheetTicket): ReadonlyArray<NamedRow> {
  const byZoneIndex = new Map<number, NamedRow>()
  const byZoneTitle = new Map<string, NamedRow>()

  const rowFor = (zoneIndex: number, title: string): NamedRow => {
    const existing = byZoneIndex.get(zoneIndex)
    if (existing) return existing
    const row: NamedRow = new Map()
    addTicketIdentity(row, ticket)
    row.set('Zone #', String(zoneIndex + 1))
    row.set('Zone', title)
    byZoneIndex.set(zoneIndex, row)
    byZoneTitle.set(title, row)
    return row
  }

  ticket.sections.forEach((section) => {
    section.zoneInventory?.forEach((zone) => {
      const row = rowFor(zone.zoneIndex, zone.title)
      if (zone.description) row.set('Zone Description', zone.description)
      if (zone.designAndFunction) {
        addKeyValueTable(row, 'Design', zone.designAndFunction)
      }
      if (zone.damageMechanisms) {
        addPeerTable(row, 'Damage Mechanisms', zone.damageMechanisms)
      }
    })

    section.zoneAnalysis?.zones.forEach((zone) => {
      const row = rowFor(zone.zoneIndex, zone.title)
      if (zone.designAndFunction) {
        addKeyValueTable(row, 'Design', zone.designAndFunction)
      }
      if (zone.damageMechanisms) {
        addPeerTable(row, 'Damage Mechanisms', zone.damageMechanisms)
      }
      if (zone.rba) addKeyValueTable(row, 'RBA', zone.rba)
      if (zone.coverageRequirements) {
        addKeyValueTable(row, 'Coverage', zone.coverageRequirements)
      }
      if (zone.strategy) {
        if (zone.strategy.overallDecision) {
          row.set('Strategy Decision', zone.strategy.overallDecision)
        }
        addPeerTable(row, 'Strategy', zone.strategy.table)
      }
      zone.overrides?.forEach((override) => {
        const name = columnName('Override', override.label)
        row.set(name, override.value)
        const details: ReadonlyArray<[string, string | undefined]> = [
          ['calculated', override.calculated],
          ['justification', override.justification],
          ['by', override.by],
          ['at', override.at],
        ]
        details.forEach(([suffix, value]) => {
          if (value) row.set(`${name} (${suffix})`, value)
        })
      })
      // `formulaLines` and `modifiers` are dropped — they restate the inputs
      // already in the Coverage columns as display strings.
      if (zone.coverageBreakdown) {
        row.set('Required Coverage', zone.coverageBreakdown.resultPercent)
        row.set('Required Coverage Basis', zone.coverageBreakdown.resultSubtext)
      }
    })

    // Zone recommendations live in the closeout section but are zone-grained,
    // and carry a title rather than an index — so they join on title.
    section.reportCloseout?.zoneRecommendations.forEach((recommendation) => {
      const row = byZoneTitle.get(recommendation.title)
      row?.set(
        'Zone Recommendations',
        joinCell(recommendation.zone_recommendations)
      )
    })
  })

  return [...byZoneIndex.values()]
}

function buildHistoryRows(ticket: NiiSheetTicket): ReadonlyArray<NamedRow> {
  const rows: NamedRow[] = []
  ticket.sections.forEach((section) => {
    section.arrayTables?.forEach((table) => {
      table.entries.forEach((entry) => {
        const row: NamedRow = new Map()
        addTicketIdentity(row, ticket)
        row.set('Table', table.title)
        entry.cells.forEach((cell) => setUnique(row, cell.label, cell.value))
        rows.push(row)
      })
    })
  })
  return rows
}

/**
 * Reconciles rows that may carry different columns into one grid. Headers are
 * in first-appearance order and missing cells become blank, so tickets from
 * different workflows can share a sheet without lining up exactly.
 */
function toSheetTable(rows: ReadonlyArray<NamedRow>): SheetTable {
  const headers: string[] = []
  const seen = new Set<string>()
  rows.forEach((row) =>
    row.forEach((_value, name) => {
      if (seen.has(name)) return
      seen.add(name)
      headers.push(name)
    })
  )
  return {
    headers,
    rows: rows.map((row) => headers.map((name) => row.get(name) ?? '')),
  }
}

/**
 * Flattens NII report content into spreadsheet rows.
 *
 * The content has two grains — ticket-level and zone-level — plus a child
 * collection (inspection history). One sheet can't hold them without either
 * repeating every ticket field per zone or dropping zones, so this produces
 * three flat sheets joined by `Ticket ID`, which is how a spreadsheet
 * expresses nesting.
 *
 * Four rules cover the whole document:
 *
 * 1. A labeled scalar becomes one column. Most of the content already is one:
 *    `{label, value}` form rows, `{question, answer}` screening answers, and
 *    `[Parameter, Value]` tables like `designAndFunction` are all the same
 *    shape wearing different names.
 * 2. A collection of entities (zones, history entries) becomes its own sheet.
 * 3. A table whose rows are peers (damage mechanisms, strategy questions), or
 *    a short string array, joins into a single cell — one column per header,
 *    keeping the peer collection inside its parent row.
 * 4. Zone images and derived display strings (coverage formula lines, zone
 *    colors) are dropped.
 *
 * Columns are discovered from the data rather than hardcoded, so a workflow
 * that gains a field appears in the sheet with no change here. Order is
 * first-appearance, which is workflow order.
 */
export function buildNiiSheetRows(
  tickets: ReadonlyArray<NiiSheetTicket>
): NiiSheetWorkbookRows {
  return {
    tickets: toSheetTable(tickets.map(buildTicketRow)),
    zones: toSheetTable(tickets.flatMap(buildZoneRows)),
    history: toSheetTable(tickets.flatMap(buildHistoryRows)),
  }
}
